import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, provide, watch, type Ref } from 'vue';

import { api } from '@api';
import { modals } from '@modals';
import { alert } from '@services/alert';
import * as models from '@models';
import { Calendar, CalendarHeader } from '@modules/calendar';

import type {
  CalendarItem,
  CalendarProps,
  PeriodChangedCallback,
} from '@modules/calendar';

import { getDateKey } from './get-date-key';

import DayUsagePanel from './DayUsagePanel.vue';
import UsageTypeEmblem from './UsageTypeEmblem.vue';
import WidgetSidebar, {
  type DeleteInviteEvent,
  type RemoveStudentEvent,
} from './WidgetSidebar.vue';

/**
 * `LicenseUsageCalendarWidget` component properties.
 */
export interface Props {
  /**
   * The ID of the organization to display usage data for.
   */
  organizationId: string;
  /**
   * If provided, the widget will initialize with the given day focused in the
   * calendar, and selected (if it exists in the usage date map).
   */
  targetDate?: string | Date;
  /**
   * If provided, the widget will display the courses for the given organization.
   */
  showCourses?: boolean;
}

export interface CourseMember {
  email: string;
  label: string | null;
  courseId?: string;
  inviteId?: string;
  studentId?: string;
}

export interface DayUsageCourseInfo {
  id: string;
  name: string;
  members: Record<string, CourseMember>;
}

export interface DayUsageInfo {
  key: string;
  date: Date;
  type: 'historical' | 'projected';
  usedStudentLicenses: number;
  maxStudentLicenses: number;
  usedInstructorLicenses: number;
  maxInstructorLicenses: number;
  usedAdminLicenses: number;
  maxAdminLicenses: number;
  courses: DayUsageCourseInfo[];
}

export type UsageDateMap = Ref<Record<string, DayUsageInfo>>;

interface DateRange {
  start: Date;
  end: Date;
}

// for (let i = 0; i < 8; i++) {
//   void api.courses.create({
//     organizationId: props.organizationId,
//     name: `Simple Course ${i + 2}`,
//     teacherId: 'a312a548-d32b-420e-bfd0-0be7bc0639bc',
//     startDate: '2025-06-02T06:00:00.000Z',
//     endDate: '2025-06-03T06:00:00.000Z',
//   });
// }

/**
 * Date range value that should always be set to the current display period
 * displayed by the calendar.
 */
interface HistoricalUsageSearchResponse {
  lastEvaluatedKey: string;
  items: models.HistoricalLicenseUsage[];
}


const __default__ = { name: 'LicenseUsageCalendarWidget' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    organizationId: null,
    targetDate: null,
    showCourses: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  /**
   * The ID of the organization to display usage data for.
   */
  organizationId: string;
  /**
   * If provided, the widget will initialize with the given day focused in the
   * calendar, and selected (if it exists in the usage date map).
   */
  targetDate?: string | Date;
  /**
   * If provided, the widget will display the courses for the given organization.
   */
  showCourses?: boolean;
};



provide('organizationId', props.organizationId);

const orgDataLoaded = ref(false);
const initialized = ref(false);
const loading = ref(false);

const showCourses = ref(props.showCourses ?? false);

const showDate = ref<Date | null>(
  props.targetDate ? new Date(props.targetDate) : new Date(),
);

const organization = ref<models.Organization | null>(null);
const courses = ref<models.Course[]>([]);

const usedStudentLicenses = ref(0);
const usedInstructorLicenses = ref(0);
const usedAdminLicenses = ref(0);

const organizationLicensingMethod = ref<
  models.Organization['licensingMethod'] | null
>(null);

const displayDateRange = ref<DateRange | null>(null);

watch(displayDateRange, getUsageForDateRange);

/**
 * Map object that correlates license usage data to a given calendar date.
 */
const usageDateMap = ref({}) as UsageDateMap;

provide('usageDateMap', usageDateMap);

const selectedDay = ref<Date | null>(null);

const courseStudentsDateMap = ref<Record<string, models.User[]>>({});

async function getCourseStudents(courseId: string) {
  if (courseStudentsDateMap.value[courseId]) {
    return courseStudentsDateMap.value[courseId];
  }

  const students = await api.users.listByCourse({
    courseId,
    organizationId: props.organizationId,
  });

  courseStudentsDateMap.value[courseId] = students;

  return students;
}

const selectedUsageInfo = computed(() => {
  const date = selectedDay.value;

  if (!date) return null;

  const key = date.toISOString();

  return usageDateMap.value[key] ?? null;
});

provide('selectedUsageInfo', selectedUsageInfo);
provide('usedStudentLicenses', usedStudentLicenses);
provide('usedInstructorLicenses', usedInstructorLicenses);
provide('usedAdminLicenses', usedAdminLicenses);
provide('organizationLicensingMethod', organizationLicensingMethod);

const courseItems = computed(() => {
  const items: CalendarItem[] = [];

  if (!showCourses.value) return items;

  for (const course of courses.value) {
    const startDate = new Date(course.startDate);
    const endDate = new Date(course.endDate);

    items.push({
      id: `course:${course.id}`,
      startDate,
      endDate,
      title: course.name,
      classes: ['course'],
    });
  }

  // Test loop for creating series of placeholder courses items.
  // for (let i = 0; i < 10; i++) {
  //   items.push({
  //     id: `course:${i}`,
  //     title: `Fake Course A ${i}`,
  //     startDate: getNormalizedDate(new Date('2025-06-16')),
  //     endDate: getNormalizedDate(new Date('2025-06-18')),
  //   });
  // }

  // for (let i = 0; i < 6; i++) {
  //   items.push({
  //     id: `course:b${i}`,
  //     title: `Fake Course B ${i}`,
  //     startDate: getNormalizedDate(new Date('2025-06-20')),
  //     endDate: getNormalizedDate(new Date('2025-06-20')),
  //   });
  // }

  return items;
});

const calendarProps = computed(() => {
  const value: CalendarProps = {
    items: courseItems.value,
  };

  if (showDate.value) {
    value.showDate = showDate.value;
  }

  return value;
});

//#region Historical Usage
async function getHistoricalUsage(
  options: api.organizations.SearchHistoricalLicenseUsageOptions,
) {
  const result = (await api.organizations.searchHistoricalLicenseUsage(
    options,
  )) as HistoricalUsageSearchResponse;

  return result.items;
}

//#endregion Historical Usage

/**
 * Gets the past (historical) usage data for the given date range and updates
 * the usage date map with the results.
 *
 * @param startDate The start date of the date range.
 * @param endDate The end date of the date range.
 */
async function getPastUsage(startDate: Date, endDate: Date) {
  const historicalUsage = await getHistoricalUsage({
    organizationId: props.organizationId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });

  const updatedMap = { ...usageDateMap.value };

  for (const { date, ...usageInfo } of historicalUsage) {
    const normalizedDate = getNormalizedDate(date);
    const key = getDateKey(normalizedDate);

    // TODO: Come up with a better way of designating/labeling anonymous courses
    // and users.
    const courses = usageInfo.studentCourseInfo.map((count, index) => {
      const members: Record<string, CourseMember> = {};

      for (let i = 0; i < count; i++) {
        const email = `user${i}@example.com`;
        const label = `Anonymous User ${i + 1}`;

        members[email] = { email, label };
      }

      return {
        id: `anonymous-course-${index + 1}`,
        name: `Anonymous Course ${index + 1}`,
        members,
      };
    });

    updatedMap[key] = {
      key,
      // TODO (Review accurate date)
      date: new Date(key),
      type: 'historical',
      ...usageInfo,
      usedAdminLicenses: usageInfo.usedAdminLicenses ?? 0,
      courses,
    };
  }

  usageDateMap.value = updatedMap;
}

/**
 * Gets future (projected) license usage data for a date range beyond the
 * current date and updates the usage date map with the results.
 *
 * @param startDate The start date of the date range.
 * @param endDate The end date of the date range.
 */
async function getFutureUsage(startDate: Date, endDate: Date) {
  const invites = await api.invites.getByOrganization({
    organizationId: props.organizationId,
    courseStartDate: getDateKey(startDate),
    courseEndDate: getDateKey(endDate),
  });

  const updatedMap = { ...usageDateMap.value };

  let maxStudentLicenses = 0;
  let maxInstructorLicenses = 0;
  let maxAdminLicenses = 0;

  let usedInstructorLicenses = 0;
  let usedAdminLicenses = 0;

  if (organization.value) {
    maxStudentLicenses = organization.value.maxStudentLicenses;
    maxInstructorLicenses = organization.value.maxInstructorLicenses;
    maxAdminLicenses = organization.value.maxAdminLicenses;

    usedInstructorLicenses = organization.value.usedInstructorLicenses;
    usedAdminLicenses = organization.value.usedAdminLicenses;
  }

  const date = new Date(startDate);

  // TODO: this method of building out records representing future student
  // license usage for a given day could be FAR more efficient. Revisit when
  // time permits.

  while (date.getTime() <= endDate.getTime()) {
    const normalizedDate = getNormalizedDate(date);
    const key = getDateKey(normalizedDate);

    const dayUsageInfo =
      updatedMap[key] ??
      (updatedMap[key] = {
        key,
        date: new Date(key),
        type: 'projected',
        usedStudentLicenses: 0,
        maxStudentLicenses,
        usedInstructorLicenses,
        maxInstructorLicenses,
        usedAdminLicenses,
        maxAdminLicenses,
        courses: [],
      });

    const studentEmails: string[] = [];

    for (const { id, course, email, accepted, receiver } of invites) {
      if (!course) {
        continue;
      }

      const courseStartDate = getNormalizedDate(course.startDate);
      const courseEndDate = getNormalizedDate(course.endDate);

      if (normalizedDate < courseStartDate || normalizedDate > courseEndDate) {
        continue;
      }

      const courseStudents = await getCourseStudents(course.id);

      // If the invite has been previously accepted by the student, but they do
      // not appear in the up-to-date list of students in the course, this
      // indicates that the student has been removed from the course, and thus,
      // should not be included in usage tabulation.
      if (accepted && !courseStudents.some(({ id }) => id === receiver?.id)) {
        continue;
      }

      if (!studentEmails.includes(email)) {
        studentEmails.push(email);
      }

      let courseInfo = dayUsageInfo.courses.find((c) => c.id === course.id);

      if (!courseInfo) {
        courseInfo = {
          id: course.id,
          name: course.name,
          members: {},
        };

        dayUsageInfo.courses.push(courseInfo);
      }

      if (!(email in courseInfo.members)) {
        const member: CourseMember = {
          inviteId: id,
          courseId: course.id,
          email,
          label: email,
        };

        if (receiver) {
          member.studentId = receiver.id;
        }

        courseInfo.members[email] = member;
      }
    }

    dayUsageInfo.usedStudentLicenses = studentEmails.length;

    date.setDate(date.getDate() + 1);
  }

  usageDateMap.value = updatedMap;
}

/**
 * Will attempt to load the usage data for the given date range.
 *
 * TODO: Implement system that tracks the range over which usage data has
 * already been loaded for, and prevent unnecessary re-loads in cases where the
 * passed date range is a subset of the previously loaded range.
 */
async function getUsageForDateRange(dateRange: DateRange | null) {
  if (!dateRange) return;

  const today = new Date();

  loading.value = true;

  // Load organization and course data if it hasn't been loaded yet.
  if (!orgDataLoaded.value) {
    organization.value = await api.organizations.get({
      organizationId: props.organizationId,
    });

    if (organization.value) {
      usedStudentLicenses.value = organization.value.usedStudentLicenses;
      usedInstructorLicenses.value = organization.value.usedInstructorLicenses;
      usedAdminLicenses.value = organization.value.usedAdminLicenses;

      organizationLicensingMethod.value = organization.value.licensingMethod;
    }

    courses.value = await api.courses.listByOrganization({
      organizationId: props.organizationId,
    });

    orgDataLoaded.value = true;
  }

  // Load historical usage data if the start date is before today.
  if (dateRange.start <= today) {
    const startDate = dateRange.start;
    const endDate = dateRange.end > today ? today : dateRange.end;

    await getPastUsage(startDate, endDate);
  }

  // Load future usage data if the end date is after today.
  if (dateRange.end >= today) {
    const startDate = dateRange.start < today ? today : dateRange.start;
    const endDate = dateRange.end;

    await getFutureUsage(startDate, endDate);
  }

  if (!initialized.value) {
    // Set selected day if target date is provided and it's in the usage date
    // map
    if (props.targetDate) {
      const dateKey = getDateKey(props.targetDate);

      if (dateKey in usageDateMap.value) {
        selectedDay.value = new Date(dateKey);
      }
    }

    initialized.value = true;
  }

  loading.value = false;

  // loadedUsageMinDate.value = dateRange.start; loadedUsageMaxDate.value =
  // dateRange.end;
}

const onPeriodChanged: PeriodChangedCallback = (periodRange) => {
  const start = new Date(periodRange.displayFirstDate);

  const end = new Date(periodRange.displayLastDate);
  end.setDate(end.getDate() - 1);

  displayDateRange.value = { start, end };
};

function closeSidebar() {
  selectedDay.value = null;
}

function onClickDay(value: Date) {
  // Takes the input date from the click-day event and sets it (only if there is
  // usage data for that date).

  const key = getDateKey(value);

  const data = usageDateMap.value[key];

  if (!data) return;

  const totalUsedLicenses =
    data.usedStudentLicenses +
    data.usedInstructorLicenses +
    data.usedAdminLicenses;

  if (totalUsedLicenses <= 0) return;

  selectedDay.value = value;
}

function setShowDate(d: Date | null) {
  showDate.value = d;
}

async function deleteInvite(data: DeleteInviteEvent) {
  const confirmed = await modals.util.confirm({
    text: 'Are you sure you want to revoke this invite?',
  });

  if (!confirmed) return;

  let error: Error | null = null;

  try {
    await api.invites.del({
      inviteId: data.inviteId,
      organizationId: props.organizationId,
    });
  } catch (e) {
    error = e as Error;
  }

  if (error) {
    return alert.error(error);
  }

  removeCourseMemberFromUsageMap(data.courseId, data.inviteEmail);

  alert.success('Invite revoked successfully.');
}

async function removeStudent(data: RemoveStudentEvent) {
  const confirmed = await modals.util.confirm({
    text: 'Are you sure you want to remove this student from the course?',
  });

  if (!confirmed) return;

  let error: Error | null = null;

  try {
    await api.courses.removeStudents({
      courseId: data.courseId,
      organizationId: props.organizationId,
      userIds: [data.studentId],
    });
  } catch (e) {
    error = e as Error;
  }

  if (error) {
    return alert.error(error);
  }

  removeCourseMemberFromUsageMap(data.courseId, data.inviteEmail);

  alert.success('Student removed successfully.');
}

/**
 * Removes a student from a course in the usage map.
 *
 * @param courseId The ID of the course to remove the student from.
 * @param email The email of the student to remove from the course.
 */
function removeCourseMemberFromUsageMap(courseId: string, email: string) {
  const updatedMap = { ...usageDateMap.value };

  let targetCourse: DayUsageCourseInfo | null = null;

  for (const info of Object.values(updatedMap)) {
    // Only projected usage data should be modified.
    if (info.type !== 'projected') {
      continue;
    }

    // Counter for the number of courses a student with the provided email is
    // a member of on the given day.
    let courseMembershipCount = 0;

    for (const course of info.courses) {
      if (!(email in course.members)) continue;

      courseMembershipCount++;

      // If the course is the one being targeted, remove the student from the
      // course.
      if (course.id === courseId) {
        targetCourse = course;
      }
    }

    if (targetCourse && courseMembershipCount === 1) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete targetCourse.members[email];

      info.usedStudentLicenses--;
    }
  }

  usageDateMap.value = updatedMap;
}

//#region Helper Functions

function getNormalizedDate(date: string | Date) {
  const dateString = typeof date === 'string' ? date : date.toISOString();

  return new Date((dateString.split('T').at(0) as string) + 'T00:00:00.000');
}

//#endregion Helper Functions

return { __sfc: true,props, orgDataLoaded, initialized, loading, showCourses, showDate, organization, courses, usedStudentLicenses, usedInstructorLicenses, usedAdminLicenses, organizationLicensingMethod, displayDateRange, usageDateMap, selectedDay, courseStudentsDateMap, getCourseStudents, selectedUsageInfo, courseItems, calendarProps, getHistoricalUsage, getPastUsage, getFutureUsage, getUsageForDateRange, onPeriodChanged, closeSidebar, onClickDay, setShowDate, deleteInvite, removeStudent, removeCourseMemberFromUsageMap, getNormalizedDate, Calendar, CalendarHeader, DayUsagePanel, UsageTypeEmblem, WidgetSidebar }
}

})